import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import {
  CollectorListItem,
  CollectorsConfig
} from '../../domain/collector-profile.model';
import { AlertService } from '../../services/alert.service';
import { CollectorProfileService } from '../../services/collector-profile.service';

@Component({
  selector: 'app-collector-expanded-detail',
  templateUrl: './collector-expanded-detail.component.html',
  styleUrls: ['./collector-expanded-detail.component.css']
})
export class CollectorExpandedDetailComponent implements OnInit, OnDestroy {
  @Input() collector!: CollectorListItem;
  @Input() config!: CollectorsConfig;

  private readonly collectorChange$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private collectorChangeVersion = 0;

  constructor(
    private collectorProfileService: CollectorProfileService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.prepareCollectorForEditing();
    this.collectorChange$
      .pipe(
        debounceTime(500),
        switchMap(() => this.persistCollector(this.collectorChangeVersion)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.collectorChange$.complete();
  }

  onCollectorChanged() {
    this.collectorChangeVersion += 1;
    this.collectorChange$.next();
  }

  private persistCollector(changeVersion: number) {
    const collectorPatch = this.createCollectorPatch();
    return this.collectorProfileService
      .updateCollector(this.collector.auth0Id, collectorPatch)
      .pipe(
        tap((updatedCollector) => {
          if (changeVersion !== this.collectorChangeVersion) {
            return;
          }
          Object.assign(this.collector, updatedCollector);
          this.prepareCollectorForEditing();
          this.syncConfigOptions(updatedCollector);
        }),
        catchError((error) => {
          const errorMessage =
            error?.error?.message ??
            error?.message ??
            'Could not save collector';
          this.alertService.showError(errorMessage);
          return EMPTY;
        })
      );
  }

  private createCollectorPatch() {
    return {
      discordAlias: this.collector.discordAlias ?? '',
      team: this.collector.team ?? '',
      role: this.collector.role ?? '',
      reliability: this.collector.reliability ?? 'NONE',
      eventSkills: { ...(this.collector.eventSkills ?? {}) },
      eventCombinations: { ...(this.collector.eventCombinations ?? {}) }
    };
  }

  private prepareCollectorForEditing() {
    this.collector.discordAlias = this.collector.discordAlias ?? '';
    this.collector.team = this.collector.team ?? '';
    this.collector.role = this.collector.role ?? '';
    this.collector.reliability = this.collector.reliability ?? 'NONE';
    this.collector.engagement = this.collector.engagement ?? 'NONE';

    const eventSkills = { ...(this.collector.eventSkills ?? {}) };
    for (const eventSkillCode of this.config.eventSkillCodes) {
      const currentValue = eventSkills[eventSkillCode];
      eventSkills[eventSkillCode] =
        currentValue && this.config.eventSkillOptions.includes(currentValue)
          ? currentValue
          : 'NONE';
    }
    this.collector.eventSkills = eventSkills;

    const eventCombinations = { ...(this.collector.eventCombinations ?? {}) };
    for (const field of this.config.eventCombinationFields) {
      eventCombinations[field] = Boolean(eventCombinations[field]);
    }
    this.collector.eventCombinations = eventCombinations;
  }

  private syncConfigOptions(collector: CollectorListItem) {
    if (collector.team && !this.config.teamOptions.includes(collector.team)) {
      this.config.teamOptions = [
        ...this.config.teamOptions,
        collector.team
      ].sort((left, right) => left.localeCompare(right));
    }
    if (collector.role && !this.config.roleOptions.includes(collector.role)) {
      this.config.roleOptions = [
        ...this.config.roleOptions,
        collector.role
      ].sort((left, right) => left.localeCompare(right));
    }
  }
}
